import type { TipoDomandaDTO, TipoUtenteDTO } from '@framework/api-client';
import {
  hasOnlyLegalEntityRecipients,
  isInquiryInTesting,
} from '@framework/common-web';
import type { SelectedLegalEntity } from '../../legal-entity/useSelectedLegalEntity';

/**
 * Regole di accreditamento per l'accesso alla compilazione (cfr. #57527):
 * per le domande esclusive per Persone Giuridiche con `flRichiedeAccreditamento`
 * attivo, la compilazione è consentita solo alla Persona Giuridica accreditata
 * con l'id della domanda tra i suoi `procedimentiAbilitati`.
 */
export class SubmissionAccreditationLogic {
  /**
   * true se tutti i destinatari della domanda sono riconducibili a Persone
   * Giuridiche. Finché i tipi utente non sono caricati il vincolo è considerato
   * attivo: il flag è ammesso solo su domande esclusive per Persone Giuridiche,
   * quindi è più sicuro partire bloccati che mostrare il pulsante abilitato.
   */
  static isLegalEntityExclusive(
    inquiry: TipoDomandaDTO,
    userKinds: TipoUtenteDTO[] | undefined,
  ): boolean {
    const recipients = inquiry.tipiUtenteCardinalita ?? [];
    if (recipients.length === 0) return false;
    // Tipi utente non caricati: vincolo considerato attivo
    // (fail-closed lato accesso).
    if (!userKinds) return true;

    return hasOnlyLegalEntityRecipients(recipients, userKinds);
  }

  /**
   * true se l'accesso alla compilazione va bloccato per mancato accreditamento.
   * Il confronto va fatto con l'id del tipo domanda: `procedimentiAbilitati`
   * contiene gli id delle domande abilitate, da non confondere con
   * `idTipoProcedimento`
   */
  static isBlockedByAccreditation(
    inquiry: TipoDomandaDTO | undefined,
    userKinds: TipoUtenteDTO[] | undefined,
    legalEntity: SelectedLegalEntity | null,
  ): boolean {
    // Una domanda in collaudo è sempre compilabile (stessa eccezione di
    // SubmissionLogic.canCreateSubmission e useIsBlockedByOB9).
    if (isInquiryInTesting(inquiry)) return false;
    if (!inquiry?.flRichiedeAccreditamento) return false;
    if (
      !SubmissionAccreditationLogic.isLegalEntityExclusive(inquiry, userKinds)
    ) {
      return false;
    }
    if (!legalEntity?.flAccreditata) return true;

    return (
      !inquiry.id ||
      !(legalEntity.procedimentiAbilitati ?? []).includes(inquiry.id)
    );
  }
}
